import {
  Button,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { InfoOIcon } from '@tonic-one/react-icons';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEmail from 'validator/lib/isEmail';

import { QuarantineApi } from '@Apis';
import { validateEmail } from '../../../utils/common';
import {
  QuarantineSettingModalName,
  QuarantineSettingModalState,
} from './interface';

const getMailboxes = (value: string) => {
  if (value.trim() === '') {
    return [];
  }
  return value.split(',').map((mailbox) => mailbox.trim());
};

const isMailboxListValid = (mailboxes: string[]) =>
  mailboxes.every((mailbox) => validateEmail(mailbox, true));

const INVALID_MAILBOX_CODES = [-1, -2, -3];

// Returns the message for a failed verify/save, or undefined when the failure is
// not one we can explain.
// InvalidInput is the BFF rejecting the DTO before validateDedicatedMailbox runs;
// it names only the field, so the offending mailboxes are re-derived locally with
// the same library backing the BFF's @IsEmail.
export const describeMailboxError = (
  e: any,
  t: TFunction,
  mailboxes: string[]
): string | undefined => {
  if (e?.data?.error?.code === 'InvalidInput') {
    const invalidMailboxes = mailboxes.filter((mailbox) => !isEmail(mailbox));
    const label = t('common.error.invalidEmailAddress');
    return invalidMailboxes.length > 0
      ? `${label}: ${invalidMailboxes.join(', ')}`
      : label;
  }
  if (e?.statusCode === -4) {
    return t('quarantine.modal.gmailSetting.validateAlert.-4', {
      maxMailBoxNum: e.maxMailBoxNum,
    });
  }
  if (INVALID_MAILBOX_CODES.includes(e?.statusCode)) {
    return t(`quarantine.modal.gmailSetting.validateAlert.${e.statusCode}`, {
      mailbox: e.invalidMailbox,
    });
  }
  return undefined;
};

export function GmailQuarantineSettingModal({
  modal,
  setModal,
}: {
  modal: QuarantineSettingModalState;
  setModal: ({ name, todo }: QuarantineSettingModalState) => void;
}) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [emailString, setEmailString] = useState('');
  const [error, setError] = useState(false);
  const [verified, setVerified] = useState(false);

  const onClose = () => {
    setModal({
      name: QuarantineSettingModalName.None,
      todo: null,
    });
  };
  useEffect(() => {
    setError(!isMailboxListValid(getMailboxes(emailString)));
  }, [emailString]);

  useEffect(() => {
    (async () => {
      if (modal.name === QuarantineSettingModalName.Gmail) {
        try {
          const emails = (
            await QuarantineApi.apiGetGmailDedicatedQuarantineSetting()
          ).data.mailBoxList;
          setEmailString(emails.join(','));
        } catch {
          window.showNotification({
            titleMsg: t('common.apiException.internalError'),
            severity: 'error',
          });
        }
      }
    })();
  }, [modal.name, t]);

  const reportMailboxFailure = useCallback(
    (e: any) => {
      window.showNotification({
        titleMsg:
          describeMailboxError(e, t, getMailboxes(emailString)) ??
          t('common.apiException.internalError'),
        severity: 'error',
      });
    },
    [emailString, t]
  );

  const onSave = async () => {
    try {
      const mailboxes = getMailboxes(emailString);
      if (!isMailboxListValid(mailboxes)) {
        setError(true);
        setVerified(false);
        return;
      }
      const result = (
        await QuarantineApi.apiPostGmailDedicatedQuarantineSetting(mailboxes)
      ).data;
      if (result.status === 'success') {
        window.showNotification({
          titleMsg: t('quarantine.modal.setting.updateSuccessfully'),
          severity: 'success',
        });
        onClose();
        return;
      }
      reportMailboxFailure(result);
    } catch (e) {
      reportMailboxFailure(e);
    }
  };
  const onVerify = async () => {
    try {
      const mailboxes = getMailboxes(emailString);
      if (!isMailboxListValid(mailboxes)) {
        setError(true);
        setVerified(false);
        return;
      }
      const result = (
        await QuarantineApi.apiVerifyGmailDedicatedMailbox(mailboxes)
      ).data;
      if (result.status === 'success') {
        setVerified(true);
        return;
      }
      setVerified(false);
      reportMailboxFailure(result);
    } catch (e) {
      setVerified(false);
      reportMailboxFailure(e);
    }
  };

  const onChange = useCallback((e: any) => {
    const value = e.target.value as string;
    setEmailString(value);
    setError(!isMailboxListValid(getMailboxes(value)));
    setVerified(false);
  }, []);

  return (
    <Modal
      closeOnEsc
      //closeOnOutsideClick
      isClosable
      isOpen={modal.name === QuarantineSettingModalName.Gmail}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent width={800}>
        <ModalHeader>{t('quarantine.modal.setting.title')}</ModalHeader>
        <ModalBody>
          <Text>{t('quarantine.modal.setting.location.title')}</Text>
          <Flex direction="column" pl="30px" gap="4px" pt="8px">
            <Radio defaultChecked>
              {t('quarantine.modal.setting.location.admin.name')}
            </Radio>
            <Text pl="24px">
              {t('quarantine.modal.setting.location.admin.descForMailbox')}
            </Text>
            <Flex align="center" pl="40px" gap="4px">
              <Text>{t('quarantine.modal.setting.label.email')}</Text>
              <Text color="red">*</Text>:
              <Flex direction="column" pt="16px">
                <Flex gap="4px">
                  <Input
                    value={emailString}
                    onChange={onChange}
                    width="400px"
                    error={error}
                  />
                  <Button onClick={onVerify} variant="primary" disabled={error}>
                    {t('common.action.label.verify')}
                  </Button>
                </Flex>
                <Text>{t('quarantine.modal.setting.tip.email')}</Text>
              </Flex>
            </Flex>
          </Flex>
          <Flex gap="8px" align="left" pl="30px" pt="20px">
            <Icon as={InfoOIcon} color={colorStyle.color.info} />
            <Text fontSize="semibold">{t('common.label.note')}:</Text>
            <Text>
              {t('quarantine.modal.setting.note')}
              <br />
              {t('quarantine.modal.setting.noteMore')}
            </Text>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            minWidth="20x"
            onClick={onSave}
            disabled={!verified && emailString.trim() !== ''}
          >
            {t('common.button.save')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
